import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  Snackbar,
} from "@mui/material";
import {
  Business as BusinessIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  ContentCopy,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { api } from "../utils/apiService";
import { API_CONFIG } from "../utils/config";
import { useAuth } from "../context/AuthContext";

const BusinessProfileForm = ({ open, onClose }) => {
  const { currentTenant, updateTenant } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [vatTouched, setVatTouched] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    businessName: "",
    alternativeCompanyName: "",
    vatNo: "",
    email: "",
    phone: "",
    tenantCode: "",
    address: "",
    shortAddress: "",
    city: "Riyadh",
    country: "Saudi Arabia",
    postalCode: "",
    streetName: "",
    buildingNumber: "",
    citySubDivision: "",
    companyRegType: "",
    companyRegNumber: "",
    image: null,
    beneficiaryName: "",
    accountNumber: "",
    bankName: "",
    iban: "",
  });

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      fetchProfileData();
      setActiveTab(0); // Reset to first tab
    }
  }, [open]);

  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/company/profile");

      if (res.data) {
        const details = res.data.companyDetails || {};
        const tenantCode = res.data.tenantCode || "";
        setFormData((prev) => ({
          ...prev,
          tenantCode: tenantCode,
          businessName: details.companyName || "",
          alternativeCompanyName: details.alternativeCompanyName || "",
          vatNo: details.vatNo || "",
          email: details.contact?.email || "",
          phone: details.contact?.phone || "",
          address: details.address?.street || "",
          shortAddress: details.address?.shortAddress || "",
          city: details.address?.city || "Riyadh",
          country: details.address?.country || "Saudi Arabia",
          postalCode: details.address?.postalCode || "",
          streetName: details.address?.street || "",
          buildingNumber: details.address?.buildingNo || "",
          citySubDivision: details.address?.subDivision || "",
          companyRegType: details.registration?.regTypeName || "",
          companyRegNumber: details.registration?.regNo || "",
          image: details.logo || null,
          beneficiaryName: details.beneficiary?.beneficiaryName || "",
          accountNumber: details.beneficiary?.accountNumber || "",
          bankName: details.beneficiary?.bankName || "",
          iban: details.beneficiary?.iban || "",
        }));
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setError(
        err.response?.data?.message || "Failed to load business profile",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTenantCode = async () => {
    if (!formData.tenantCode) {
      const warningMessage = "Tenant code is unavailable";
      if (typeof enqueueSnackbar === "function") {
        enqueueSnackbar(warningMessage, { variant: "warning" });
      }
      setSnackbar({
        open: true,
        message: warningMessage,
        severity: "warning",
      });
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(formData.tenantCode);
        const successMessage = "Tenant code copied to clipboard successfully!";
        if (typeof enqueueSnackbar === "function") {
          enqueueSnackbar(successMessage, { variant: "success" });
        }
        setSnackbar({
          open: true,
          message: successMessage,
          severity: "success",
        });
      } else {
        throw new Error("Clipboard API not available");
      }
    } catch (err) {
      console.error("Failed to copy tenant code:", err);
      const errorMessage = "Failed to copy tenant code to clipboard";
      if (typeof enqueueSnackbar === "function") {
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "vatNo") setVatTouched(true);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoading(true);
      setError(null);

      try {
        const uploadData = new FormData();
        uploadData.append("files", file);

        const response = await api.post("/upload", uploadData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        let uploadedPath = "";
        if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data) &&
          response.data.data.length > 0
        ) {
          uploadedPath = response.data.data[0];
        } else {
          uploadedPath =
            response.data?.url ||
            response.data?.imageUrl ||
            response.data?.path ||
            response.data;
        }

        if (uploadedPath && typeof uploadedPath === "string") {
          setFormData((prev) => ({
            ...prev,
            image: uploadedPath,
          }));
        } else {
          throw new Error("Invalid URL returned from server");
        }
      } catch (err) {
        console.error("Error uploading image:", err);
        setError(err.response?.data?.message || "Failed to upload image");
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http") || imagePath.startsWith("data:")) {
      return imagePath;
    }
    return `${API_CONFIG.BASE_URL}/${imagePath.replace(/^\/+/, "")}`;
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
    }));
  };
  const vatNoTrimmed = formData.vatNo.trim();
  const vatErrorMessage = (() => {
    if (vatNoTrimmed.length === 0) return "VAT Number is required";
    if (vatNoTrimmed.length !== 15)
      return "VAT Number must be exactly 15 characters";
    if (vatNoTrimmed[0] !== "3" || vatNoTrimmed[14] !== "3")
      return "VAT Number must start with 3 and end with 3";
    return "";
  })();
  const vatError = vatTouched && vatErrorMessage !== "";

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setVatTouched(true);
    const trimmedVat = formData.vatNo.trim();
    if (!trimmedVat) {
      setError("VAT Number is required");
      return;
    }
    if (trimmedVat.length !== 15) {
      setError("VAT Number must be exactly 15 characters");
      return;
    }
    if (trimmedVat[0] !== "3" || trimmedVat[14] !== "3") {
      setError("VAT Number must start with 3 and end with 3");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const apiData = {
        companyDetails: {
          companyName: formData.businessName,
          alternativeCompanyName: formData.alternativeCompanyName,
          vatNo: formData.vatNo,
          contact: {
            email: formData.email,
            phone: formData.phone,
          },
          address: {
            street: formData.streetName,
            shortAddress: formData.shortAddress,
            buildingNo: formData.buildingNumber,
            city: formData.city,
            country: formData.country,
            postalCode: formData.postalCode,
            subDivision: formData.citySubDivision,
          },
          registration: {
            regTypeName: formData.companyRegType,
            regNo: formData.companyRegNumber,
          },
          logo: formData.image,
          beneficiary: {
            beneficiaryName: formData.beneficiaryName,
            accountNumber: formData.accountNumber,
            bankName: formData.bankName,
            iban: formData.iban,
          },
        },
      };

      const res = await api.put("/company/profile", apiData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (res.data) {
        // Update local state / context if the companyDetails exists in response
        if (res.data.companyDetails && currentTenant) {
          const newTenant = {
            ...currentTenant,
            name: res.data.companyDetails.companyName,
          };
          updateTenant(newTenant);
        } else if (
          res.data.data &&
          res.data.data.companyDetails &&
          currentTenant
        ) {
          // Just in case the backend wraps it in a data object
          const newTenant = {
            ...currentTenant,
            name: res.data.data.companyDetails.companyName,
          };
          updateTenant(newTenant);
        }
        onClose();
      } else {
        throw new Error("No response data received");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(
        err.response?.data?.message || "Failed to update business profile",
      );
    } finally {
      setLoading(false);
    }
  };

  const controlStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      height: 36,
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: 0,
      },
    },
    "& .MuiInputBase-input": {
      py: 0.75,
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
    },
    "& .MuiSelect-select": {
      py: 0.75,
      borderRadius: 0,
    },
  };

  const multilineStyle = {
    ...controlStyle,
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      minHeight: 36,
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: 0,
      },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          boxShadow: "none",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "rgba(67, 97, 238, 0.02)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.5,
        }}
      >
        <BusinessIcon sx={{ color: "primary.main", fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight="bold" component="span">
          Business Profile
        </Typography>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              fontSize: "0.813rem",
              minHeight: 42,
              textTransform: "none",
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Company Info" />
          <Tab label="Address" />
          <Tab label="Bank Account" />
        </Tabs>
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2, pb: 1, overflowY: "auto", minHeight: 400 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* Logo Upload - Show on all tabs */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={formData.image ? getImageUrl(formData.image) : null}
                    sx={{
                      width: 80,
                      height: 80,
                      cursor: "pointer",
                      bgcolor: "grey.200",
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                    onClick={handleImageClick}
                  >
                    {!formData.image && <AddPhotoIcon sx={{ fontSize: 32 }} />}
                  </Avatar>
                  {formData.image && (
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        bgcolor: "error.main",
                        color: "white",
                        width: 24,
                        height: 24,
                        "&:hover": {
                          bgcolor: "error.dark",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </Box>
              </Box>

              {/* Tab 0: Company Info */}

              {activeTab === 0 && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Business Name"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Alternative Company Name"
                      name="alternativeCompanyName"
                      value={formData.alternativeCompanyName}
                      onChange={handleChange}
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="VAT Number"
                      type="text"
                      name="vatNo"
                      value={formData.vatNo}
                      onChange={handleChange}
                      required
                      error={vatError}
                      helperText={vatError ? vatErrorMessage : ""}
                      inputProps={{ maxLength: 15, inputMode: "numeric" }}
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>

                  {/* Phone and Tenant Code in a single row */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tenant Code"
                      name="tenantCode"
                      value={formData.tenantCode || ""}
                      variant="outlined"
                      sx={controlStyle}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="copy tenant code"
                              onClick={handleCopyTenantCode}
                              edge="end"
                              size="small"
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={controlStyle}>
                      <InputLabel sx={{ fontSize: "0.875rem" }}>
                        Registration Type
                      </InputLabel>

                      <Select
                        name="companyRegType"
                        value={formData.companyRegType}
                        onChange={handleChange}
                        label="Registration Type"
                      >
                        <MenuItem value="CRNO" sx={{ fontSize: "0.875rem" }}>
                          CRNO
                        </MenuItem>
                        <MenuItem value="MOM" sx={{ fontSize: "0.875rem" }}>
                          MOM
                        </MenuItem>
                        <MenuItem value="VAT" sx={{ fontSize: "0.875rem" }}>
                          VAT
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Registration Number"
                      name="companyRegNumber"
                      value={formData.companyRegNumber}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Tab 1: Address Details */}
              {activeTab === 1 && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Street Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      multiline
                      rows={2}
                      variant="outlined"
                      sx={multilineStyle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Short Address"
                      name="shortAddress"
                      value={formData.shortAddress}
                      onChange={handleChange}
                      placeholder="Brief address for invoices"
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Postal Code"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Street Name"
                      name="streetName"
                      value={formData.streetName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Building Number"
                      name="buildingNumber"
                      value={formData.buildingNumber}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City Sub Division"
                      name="citySubDivision"
                      value={formData.citySubDivision}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Tab 2: Bank Account Details */}
              {activeTab === 2 && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Account Holder Name"
                      name="beneficiaryName"
                      value={formData.beneficiaryName}
                      onChange={handleChange}
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Account Number"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleChange}
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Bank Name"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      variant="outlined"
                      sx={controlStyle}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="IBAN"
                      name="iban"
                      value={formData.iban}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="SA00 0000 0000 0000 0000 0000"
                      sx={controlStyle}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.05)",
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            disabled={loading}
            sx={{
              borderRadius: 0,
              textTransform: "none",
              px: 2,
              fontSize: "0.875rem",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={loading}
            sx={{
              borderRadius: 0,
              textTransform: "none",
              px: 2,
              fontSize: "0.875rem",
              background: "linear-gradient(45deg, #4361ee 0%, #4895ef 100%)",
              "&:hover": {
                background: "linear-gradient(45deg, #3a56d4 0%, #3f85e6 100%)",
              },
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default BusinessProfileForm;
